import { useState } from 'react'
import { Link } from 'react-router-dom'

import { FxButton, FxIcon } from '@/components/fx'
import { FieldSnippet, type ReviewQueueItem } from '@/components/review'
import { PageViewer } from '@/components/viewer/PageViewer'
import { cn } from '@/lib/utils'

import { NearViewport } from './NearViewport'
import { RowValueEditor } from './RowValueEditor'

/**
 * One flagged field as a card: what was cropped, what was read, why it is
 * here, and the one control that settles it.
 *
 * The crop is the evidence and it comes first, at the size the design draws
 * it. The page it was cut out of is the fallback, one click away **inside the
 * card**: a reviewer whose crop is ambiguous ("is that an l or a 1?") needs
 * the line above and below it, and sending them to the document screen for
 * that would cost them their place in a queue of three hundred. So the page
 * opens under the row and closes again, and the queue never moves.
 */

export interface BulkRowProps {
  item: ReviewQueueItem
  templateId: string
  /** The running number the design puts in front of the row (1-based). */
  number: number
  /** The stored page the field's rectangle may be drawn on, or null — see
   *  `previewImagePath`, which refuses the un-warped original. */
  pageImagePath: string | null
  active?: boolean
  busy?: boolean
  /** Written and awaiting the undo window: drawn as settled, not removed
   *  under the reviewer's hands. */
  settled?: boolean
  onSubmit: (value: string) => void
  onFocus?: () => void
}

export function BulkRow({
  item,
  templateId,
  number,
  pageImagePath,
  active,
  busy,
  settled,
  onSubmit,
  onFocus,
}: BulkRowProps) {
  const [showPage, setShowPage] = useState(false)
  const documentHref = `/templates/${templateId}/dokumente/${item.documentId}?feld=${encodeURIComponent(item.fieldId)}`

  return (
    <article
      aria-label={`${item.label} in ${item.documentTitle ?? item.documentId}`}
      className={cn(
        'rounded-sm bg-surface p-4 shadow-raised transition-opacity',
        // The focused card, as the artboard draws it: an inset ring rather
        // than a border, because nothing in this design outlines a card.
        active && 'shadow-[var(--fx-shadow-raised),inset_0_0_0_2px_hsl(var(--fx-primary-strong))]',
        settled && 'opacity-60',
      )}
    >
      <div className="flex flex-col gap-4 md:grid md:grid-cols-[2.5rem_17.5rem_15rem_minmax(0,1fr)] md:items-center md:gap-5">
        <span className="text-small text-text-muted">#{number}</span>

        {/* The crop is fetched when the row comes near the fold, not when the
            list mounts: see `NearViewport`. */}
        <NearViewport
          className="h-16 w-full md:w-[17.5rem]"
          placeholder={<div className="h-full w-full rounded-sm bg-fill" aria-hidden="true" />}
        >
          <FieldSnippet
            templateId={templateId}
            documentId={item.documentId}
            fieldId={item.fieldId}
            label={item.label}
            className="h-full w-full bg-ground"
          />
        </NearViewport>

        <div className="flex min-w-0 flex-col gap-1">
          <span className="font-semibold">{item.label}</span>
          <span className="truncate text-small text-text-muted" title={item.documentTitle}>
            {item.documentTitle ?? item.documentId}
          </span>
          {item.flag && (
            <span className="flex items-center gap-1 text-caption text-danger-text">
              <FxIcon name="error" size={16} />
              {item.flag.label}
            </span>
          )}
          {settled && (
            <span className="flex items-center gap-1 text-caption text-success-text">
              <FxIcon name="check_circle" size={16} />
              Gespeichert
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <RowValueEditor
            label={item.label}
            type={item.type}
            value={item.value}
            textType={item.textType}
            resetKey={item.key}
            onSubmit={onSubmit}
            busy={busy}
            active={active}
            onFocus={onFocus}
          />
          <div className="flex flex-wrap items-center gap-4 text-small">
            <FxButton
              variant="outlined"
              icon={showPage ? 'expand_less' : 'crop_free'}
              aria-expanded={showPage}
              onClick={() => setShowPage((open) => !open)}
              disabled={!pageImagePath}
            >
              {showPage ? 'Seite schließen' : 'Seite ansehen'}
            </FxButton>
            <Link to={documentHref} className="text-primary-text underline-offset-2 hover:underline">
              Im Dokument öffnen
            </Link>
            {!pageImagePath && (
              <span className="text-caption text-text-muted">Keine ausgerichtete Seite gespeichert</span>
            )}
          </div>
        </div>
      </div>

      {showPage && pageImagePath && (
        <div className="mt-4 h-[26rem]">
          <PageViewer
            imagePath={pageImagePath}
            // The stored analysed/enhanced page is in the same frame the
            // result's coordinates are written in, so the box needs no
            // scaling — `frame="image"` is exactly that statement.
            frame="image"
            boxes={item.rect ? [{ id: item.fieldId, label: item.label, rect: item.rect, tone: 'danger' }] : []}
            selectedBoxId={item.fieldId}
            showLabels
            alt={`Seite von ${item.documentTitle ?? item.documentId} mit dem Feld ${item.label}`}
            className="h-full"
          />
        </div>
      )}
    </article>
  )
}
